import { useEffect, useState } from 'react';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import { searchRequest } from './filters';

/** The pages the listing shows: the current one, or (load more) every page up to it; never past `totalPages` when known. */
export function pagesFor(page, paging, totalPages = null) {
  const last = Math.max(1, totalPages == null ? page : Math.min(page, totalPages));
  return paging === 'load_more' ? Array.from({ length: last }, (_, i) => i + 1) : [last];
}

/**
 * The listing page's results from the server (Listing.search, MERCH-86): filtered, sorted,
 * paged and counted in the query (rule 7). Pages are kept by the page the server answered
 * (`r.page`: it clamps a page past the end), so "Load more" appends instead of reloading and a
 * page past the end is never asked for again. Facets count the whole type, whatever the
 * filters, so they are kept per type: the server sends them with page 1, and a page past the
 * first asks for them (`facets: true`) only while this type has none (security review L4).
 * `enabled: false` (the type's slug is not resolved yet) holds the request.
 * → { items, total, facets, loading, error }
 */
export function useCatalogSearch(f, type, { size, paging, enabled = true }) {
  const { fetchApi } = useAppApi();
  const request = searchRequest(f, type);
  const key = JSON.stringify([request, size]);
  const typeKey = type ? type.key : '';
  const [state, setState] = useState({ key: null, pages: {}, total: 0, totalPages: null, loading: true, error: '' });
  const [facets, setFacets] = useState({});
  const mine = state.key === key;
  const wanted = pagesFor(f.page, paging, mine ? state.totalPages : null);

  useEffect(() => {
    let live = true;
    if (!enabled) return undefined;
    const have = mine ? state.pages : {};
    const missing = wanted.filter((p) => !have[p]);
    if (!missing.length) return undefined;
    const askFacets = !facets[typeKey];
    setState((s) => ({ ...s, loading: true, error: '' }));
    Promise.all(missing.map((page, i) => fetchApi('sfSearch', { ...request, page, page_size: size, ...(askFacets && i === 0 && page !== 1 ? { facets: true } : {}) }, false)))
      .then((answers) => {
        if (!live) return;
        const got = answers.find((r) => r && r.facets);
        if (got) setFacets((all) => ({ ...all, [typeKey]: got.facets }));
        setState((s) => {
          const pages = s.key === key ? { ...s.pages } : {};
          answers.forEach((r, i) => { pages[Number(r && r.page) || missing[i]] = (r && r.data) || []; });
          const last = answers[answers.length - 1] || {};
          return { key, pages, total: Number(last.total) || 0, totalPages: Number(last.total_pages) || 1, loading: false, error: '' };
        });
      })
      .catch((ex) => { if (live) setState((s) => ({ ...s, loading: false, error: errorText(ex) })); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` and the page list are the request
  }, [key, wanted.join(), enabled]);

  const pages = mine ? state.pages : {};
  return {
    items: wanted.flatMap((p) => pages[p] || []),
    total: mine ? state.total : 0,
    facets: facets[typeKey] || null,
    loading: state.loading || !mine,
    error: state.error,
  };
}
